import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from './utils/experienceStore';
import {
    loadProcessingFlow,
    resetProcessingFlow,
    redrawFlowLayout,
    ProcessorNode,
} from './utils/state/processingFlowSlice';
import {
    Processor,
    ProcessingFlow,
    ProcessingEnvironment,
} from '../Processing/components/hooks/useProcessing';
import ProcessingFlowCanvas from './ProcessingFlowCanvas';
import ProcessorPalette from './ProcessorPalette';
import ProcessorPropertiesPanel from './ProcessorPropertiesPanel';
import FlowSequencePanel from './FlowSequencePanel';

import {
    Save,
    Upload,
    Download,
    Settings,
    X,
    Zap,
    RotateCcw,
    ArrowLeft,
    Check,
} from 'lucide-react';

interface VisualProcessingFlowEditorProps {
    flow?: ProcessingFlow;
    processors: Processor[];
    environments: ProcessingEnvironment[];
    onSave?: (flowDefinition: ProcessorNode[], enabled: boolean) => void;
    onClose?: () => void;
    onLaunchAssetPicker?: (callback: (value: string) => void) => void;
    hideBackButton?: boolean;
}

export default function VisualProcessingFlowEditor({
    flow,
    processors,
    environments,
    onSave,
    onClose,
    onLaunchAssetPicker,
    hideBackButton = false,
}: VisualProcessingFlowEditorProps) {
    const dispatch = useDispatch();
    const [canvasSize, setCanvasSize] = useState({ width: 1200, height: 800 });

    const [isPropertiesOpen, setIsPropertiesOpen] = useState(true);
    const [isSequenceOpen, setIsSequenceOpen] = useState(true);

    const handleToggleProperties = () => {
        setIsPropertiesOpen(!isPropertiesOpen);
    };
    const [isEnabled] = useState(flow?.enabled ?? true);
    // eslint-disable-next-line
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [showSaveSuccess, setShowSaveSuccess] = useState(false);

    const { nodes, connections } = useSelector(
        (state: RootState) => state.flows.present,
    );

    // Filter processors for local environment only
    const localProcessors = useMemo(() => {
        return processors.filter(
            (processor) =>
                !processor.environments ||
                processor.environments.includes('local') ||
                processor.environments.length === 0,
        );
    }, [processors]);

    // Load existing flow on mount
    useEffect(() => {
        if (flow?.flowDefinition) {
            try {
                const existingFlow = JSON.parse(flow.flowDefinition);
                if (Array.isArray(existingFlow)) {
                    // Convert old format to new format
                    const convertedNodes: ProcessorNode[] = existingFlow.map(
                        (processor, index) => {
                            // Cast all argument values to strings as required by schema
                            const stringArguments: { [key: string]: string } =
                                {};
                            if (
                                processor.arguments &&
                                typeof processor.arguments === 'object'
                            ) {
                                Object.keys(processor.arguments).forEach(
                                    (key) => {
                                        stringArguments[key] = String(
                                            processor.arguments[key],
                                        );
                                    },
                                );
                            }

                            return {
                                id: `node-${Date.now()}-${index}`,
                                processor,
                                position: { x: 100 + index * 250, y: 100 },
                                arguments: stringArguments,
                                saveOutput: processor.saveOutput,
                            };
                        },
                    );

                    // Create connections between sequential nodes
                    const convertedConnections = convertedNodes
                        .slice(0, -1)
                        .map((node, index) => ({
                            id: `conn-${Date.now()}-${index}`,
                            sourceId: node.id,
                            targetId: convertedNodes[index + 1].id,
                        }));

                    dispatch(
                        loadProcessingFlow({
                            nodes: convertedNodes,
                            connections: convertedConnections,
                        }),
                    );
                }
            } catch (error) {
                console.error('Error loading existing flow:', error);
            }
        } else {
            dispatch(resetProcessingFlow());
        }
    }, [flow, dispatch]);

    // Dynamic canvas sizing using actual container dimensions
    useEffect(() => {
        const updateCanvasSize = () => {
            const container = document.getElementById('flow-canvas-container');
            if (container) {
                // Force layout recalculation
                container.style.width = '';
                const rect = container.getBoundingClientRect();

                // Use actual container dimensions
                setCanvasSize({
                    width: rect.width || 400,
                    height: rect.height || 300,
                });
            }
        };

        // Use requestAnimationFrame for smoother updates
        const scheduleUpdate = () => {
            requestAnimationFrame(() => {
                updateCanvasSize();
            });
        };

        // Initial calculation
        scheduleUpdate();

        // Multiple updates to handle panel transition timing
        const timeouts = [
            setTimeout(scheduleUpdate, 0),
            setTimeout(scheduleUpdate, 100),
            setTimeout(scheduleUpdate, 200),
            setTimeout(scheduleUpdate, 400),
        ];

        // Window resize handler
        window.addEventListener('resize', scheduleUpdate);

        return () => {
            timeouts.forEach(clearTimeout);
            window.removeEventListener('resize', scheduleUpdate);
        };
    }, [isPropertiesOpen, isSequenceOpen]);

    // Convert nodes to old format for saving
    const convertToOldFormat = useCallback((): Processor[] => {
        // Sort nodes by their connections to maintain flow order
        const sortedNodes: ProcessorNode[] = [];
        const visited = new Set<string>();

        // Find start nodes (nodes with no incoming connections)
        const startNodes = nodes.filter(
            (node) => !connections.some((conn) => conn.targetId === node.id),
        );

        // Depth-first traversal to maintain order
        const traverse = (nodeId: string) => {
            if (visited.has(nodeId)) return;

            const node = nodes.find((n) => n.id === nodeId);
            if (!node) return;

            visited.add(nodeId);
            sortedNodes.push(node);

            // Find connected nodes
            const outgoingConnections = connections.filter(
                (conn) => conn.sourceId === nodeId,
            );
            outgoingConnections.forEach((conn) => traverse(conn.targetId));
        };

        // Start traversal from all start nodes
        startNodes.forEach((node) => traverse(node.id));

        // Add any remaining unconnected nodes
        nodes.forEach((node) => {
            if (!visited.has(node.id)) {
                sortedNodes.push(node);
            }
        });

        // Convert to old format with string arguments
        return sortedNodes.map((node) => {
            // Ensure all arguments are strings
            const stringArguments: { [key: string]: string } = {};
            if (node.arguments && typeof node.arguments === 'object') {
                Object.keys(node.arguments).forEach((key) => {
                    stringArguments[key] = String(node.arguments[key]);
                });
            }

            return {
                ...node.processor,
                arguments: stringArguments,
                saveOutput: node.saveOutput,
            };
        });
    }, [nodes, connections]);

    const handleSave = () => {
        if (onSave) {
            onSave(nodes, isEnabled);

            // Show success indicator
            setShowSaveSuccess(true);

            // Hide after 2 seconds
            setTimeout(() => {
                setShowSaveSuccess(false);
            }, 2000);
        }
    };

    const handleExport = () => {
        const flowDefinition = convertToOldFormat();
        const dataStr = JSON.stringify(flowDefinition, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);

        const link = document.createElement('a');
        link.href = url;
        link.download = `processing-flow-${Date.now()}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        setShowExportDialog(false);
    };

    const handleImport = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (e) => {
                    try {
                        const text = e.target?.result as string;
                        const importedFlow = JSON.parse(text);

                        if (Array.isArray(importedFlow)) {
                            // Convert imported flow to new format
                            const convertedNodes: ProcessorNode[] =
                                importedFlow.map((processor, index) => {
                                    // Cast all argument values to strings as required by schema
                                    const stringArguments: {
                                        [key: string]: string;
                                    } = {};
                                    if (
                                        processor.arguments &&
                                        typeof processor.arguments === 'object'
                                    ) {
                                        Object.keys(
                                            processor.arguments,
                                        ).forEach((key) => {
                                            stringArguments[key] = String(
                                                processor.arguments[key],
                                            );
                                        });
                                    }

                                    return {
                                        id: `node-${Date.now()}-${index}`,
                                        processor,
                                        position: {
                                            x: 100 + index * 250,
                                            y: 100,
                                        },
                                        arguments: stringArguments,
                                        saveOutput: processor.saveOutput,
                                    };
                                });

                            // Create connections between sequential nodes
                            const convertedConnections = convertedNodes
                                .slice(0, -1)
                                .map((node, index) => ({
                                    id: `conn-${Date.now()}-${index}`,
                                    sourceId: node.id,
                                    targetId: convertedNodes[index + 1].id,
                                }));

                            dispatch(
                                loadProcessingFlow({
                                    nodes: convertedNodes,
                                    connections: convertedConnections,
                                }),
                            );
                        }
                    } catch (error) {
                        console.error('Error importing flow:', error);
                        alert(
                            'Error importing flow. Please check the file format.',
                        );
                    }
                };
                reader.readAsText(file);
            }
        };
        input.click();
    };

    const propertiesPanelWidth = isPropertiesOpen ? 320 : 60; // 60px for collapsed state
    const sequencePanelWidth = isSequenceOpen ? 280 : 0;

    return (
        <div className="h-full w-full flex flex-col bg-gray-100 overflow-hidden">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-300 px-4 py-3 flex items-center justify-between shadow-sm">
                <div className="flex items-center space-x-4">
                    {onClose && !hideBackButton && (
                        <button
                            onClick={onClose}
                            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
                            title="Back to Experience Editor"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            <span>Back to Experience Editor</span>
                        </button>
                    )}
                    {onClose && !hideBackButton && (
                        <div className="w-px h-6 bg-gray-300" />
                    )}
                </div>

                <div className="flex items-center space-x-2">
                    {/* Toggle Buttons */}
                    <button
                        onClick={() => setIsSequenceOpen(!isSequenceOpen)}
                        className={`p-2 rounded-md transition-colors ${
                            isSequenceOpen
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Toggle Flow Sequence Panel"
                    >
                        <Zap className="w-4 h-4" />
                    </button>

                    <button
                        onClick={() => setIsPropertiesOpen(!isPropertiesOpen)}
                        className={`p-2 rounded-md transition-colors ${
                            isPropertiesOpen
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                        title="Toggle Properties Panel"
                    >
                        <Settings className="w-4 h-4" />
                    </button>

                    <div className="w-px h-6 bg-gray-300" />

                    {/* Layout Button */}
                    <button
                        onClick={() => dispatch(redrawFlowLayout())}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                        title="Redraw flow in sequential order"
                    >
                        <RotateCcw className="w-4 h-4" />
                        <span>Redraw</span>
                    </button>

                    {/* Action Buttons */}
                    <button
                        onClick={handleImport}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        <Upload className="w-4 h-4" />
                        <span>Import</span>
                    </button>

                    <button
                        onClick={handleExport}
                        className="flex items-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                    </button>

                    {onSave && (
                        <button
                            onClick={handleSave}
                            className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-all ${
                                showSaveSuccess
                                    ? 'bg-green-600 hover:bg-green-700'
                                    : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}
                        >
                            {showSaveSuccess ? (
                                <>
                                    <Check className="w-4 h-4" />
                                    <span>Applied!</span>
                                </>
                            ) : (
                                <>
                                    <Save className="w-4 h-4" />
                                    <span>Apply</span>
                                </>
                            )}
                        </button>
                    )}

                    {onClose && (
                        <button
                            onClick={onClose}
                            className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Main Content */}
            <div
                className="flex-1 flex overflow-hidden"
                style={{ maxWidth: '100vw' }}
            >
                {/* Processor Palette - Fixed Left Panel */}
                <div className="flex-shrink-0 h-full">
                    <ProcessorPalette
                        processors={localProcessors}
                        width={280}
                    />
                </div>

                {/* Flow Sequence Panel - Left-Center Panel */}
                {isSequenceOpen && (
                    <div className="flex-shrink-0">
                        <FlowSequencePanel width={sequencePanelWidth} />
                    </div>
                )}

                {/* Canvas */}
                <div
                    id="flow-canvas-container"
                    className="flex-1 relative min-w-0 overflow-hidden"
                >
                    <ProcessingFlowCanvas
                        width={canvasSize.width}
                        height={canvasSize.height}
                        processors={localProcessors}
                    />
                </div>

                {/* Properties Panel - Always rendered (expanded or collapsed) */}
                <div className="flex-shrink-0">
                    <ProcessorPropertiesPanel
                        width={propertiesPanelWidth}
                        onLaunchAssetPicker={onLaunchAssetPicker}
                        onToggleCollapse={handleToggleProperties}
                    />
                </div>
            </div>

            {/* Status Bar */}
            <div className="bg-white border-t border-gray-300 px-4 py-2 flex items-center justify-between text-sm text-gray-600">
                <div className="flex items-center space-x-4">
                    <span>{nodes.length} processors</span>
                    <span>{connections.length} connections</span>
                    <span>{localProcessors.length} available processors</span>
                </div>
                <div className="flex items-center space-x-2">
                    <span
                        className={`px-2 py-1 rounded text-xs ${
                            isEnabled
                                ? 'bg-green-100 text-green-700'
                                : 'bg-gray-100 text-gray-700'
                        }`}
                    >
                        {isEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                </div>
            </div>
        </div>
    );
}
